/*
 * Copyright The OpenTelemetry Authors
 * SPDX-License-Identifier: Apache-2.0
 */

import { context } from '@opentelemetry/api';
import { SeverityNumber } from '@opentelemetry/api-logs';
import { InstrumentationBase } from '@opentelemetry/instrumentation';
import { version } from '../../package.json' with { type: 'json' };
import { ATTR_CONSOLE_METHOD, CONSOLE_LOG_EVENT_NAME } from './semconv.ts';
import type { ConsoleInstrumentationConfig, ConsoleMethod } from './types.ts';

const DEFAULT_LOG_METHODS: ConsoleMethod[] = [
  'log',
  'warn',
  'error',
  'info',
  'debug',
];

const SEVERITY_MAP: Record<ConsoleMethod, SeverityNumber> = {
  debug: SeverityNumber.DEBUG,
  log: SeverityNumber.INFO,
  info: SeverityNumber.INFO,
  warn: SeverityNumber.WARN,
  error: SeverityNumber.ERROR,
};

/**
 * Default serializer for console arguments.
 * Joins arguments as strings.
 */
function defaultMessageSerializer(args: unknown[]): string {
  return args
    .map((arg) => {
      if (typeof arg === 'object' && arg !== null) {
        try {
          return JSON.stringify(arg);
        } catch {
          // Circular reference or other error, fallback to String
          return String(arg);
        }
      }
      return String(arg);
    })
    .join(' ');
}

/**
 * OpenTelemetry instrumentation that captures console calls and emits them as OpenTelemetry logs.
 */
export class ConsoleInstrumentation extends InstrumentationBase<ConsoleInstrumentationConfig> {
  private declare _isPatched: boolean;
  private declare _active: boolean;

  constructor(config: ConsoleInstrumentationConfig = {}) {
    super('@opentelemetry/browser-instrumentation/console', version, config);
  }

  protected override init() {
    return [];
  }

  private _getMessageSerializer(): (args: unknown[]) => string {
    return this._config.messageSerializer ?? defaultMessageSerializer;
  }

  private _getLogMethods(): ConsoleMethod[] {
    return this._config.logMethods ?? DEFAULT_LOG_METHODS;
  }

  private _patchConsoleMethod(
    method: ConsoleMethod,
  ): (original: Console[ConsoleMethod]) => Console[ConsoleMethod] {
    const instrumentation = this;

    return function patchConsoleMethod(original: Console[ConsoleMethod]) {
      return function (this: Console, ...args: unknown[]) {
        if (
          instrumentation._active &&
          instrumentation._getLogMethods().includes(method)
        ) {
          const logContext = context.active();
          const body = instrumentation._getMessageSerializer()(args);

          instrumentation.logger.emit({
            body,
            eventName: CONSOLE_LOG_EVENT_NAME,
            severityNumber: SEVERITY_MAP[method],
            severityText: method,
            context: logContext,
            attributes: {
              [ATTR_CONSOLE_METHOD]: method,
            },
          });
        }

        return original.apply(this, args);
      } as Console[ConsoleMethod];
    };
  }

  override enable(): void {
    this._active = true;
    if (this._isPatched) {
      return;
    }
    this._isPatched = true;
    for (const method of DEFAULT_LOG_METHODS) {
      if (typeof console[method] === 'function') {
        this._wrap(console, method, this._patchConsoleMethod(method));
      }
    }
  }

  override disable(): void {
    this._active = false;
  }
}
